// Peptide Calculator v4.1 — Compound Database & Constants

export interface SynergyInfo {
  key: string;
  label: string;
  note: string;
}

export interface DosingTier {
  dose: number;
  doseUnit: 'mg' | 'mcg' | 'units';
  freq: FrequencyKey;
  info: string;
}

export type TierKey = 'micro' | 'low' | 'moderate' | 'high' | 'veryhigh';
export type FrequencyKey = 'daily' | '5on2off' | '3x_week' | '2x_week' | '1x_week' | 'biweekly' | 'monthly';
export type CategoryKey = 'all' | 'weight' | 'gh' | 'healing' | 'metabolic' | 'sexual';
export type RegulatoryStatus = 'fda' | 'investigational' | 'research' | 'cat2';

export interface PeptidePreset {
  name: string;
  category: Exclude<CategoryKey, 'all'>;
  conc: number;
  concUnit: 'mg/mL' | 'mcg/mL' | 'units/mL';
  vial: number;
  route: 'SubQ' | 'IM' | 'IV';
  regulatory: RegulatoryStatus;
  regNote: string;
  weightBased: boolean;
  weightNote: string;
  synergy: SynergyInfo[];
  recFreq: FrequencyKey;
  tiers: Record<TierKey, DosingTier>;
  timeline: Partial<Record<TierKey, string>>;
  warnings: Partial<Record<TierKey, string>>;
}

export interface StackCompound {
  id: number;
  name: string;
  conc: number;
  concUnit: 'mg/mL' | 'mcg/mL' | 'units/mL';
  vial: number;
  dose: number;
  doseUnit: 'mg' | 'mcg' | 'units';
  freq: FrequencyKey;
  route: 'SubQ' | 'IM' | 'IV';
  tier: TierKey | 'custom';
  tierInfo: string;
  timelineInfo: string;
  synergyNote: string;
  warnInfo: string;
  // Resolved formulary SKU (set when added from a real catalog product), so the
  // stack can be turned into an orderable list. Absent for manual entries.
  productId?: string;
  productName?: string;
  unitPrice?: number;
  outOfStock?: boolean;
}

export const FREQ_LABELS: Record<FrequencyKey, string> = {
  daily: 'Daily',
  '5on2off': '5 on / 2 off',
  '3x_week': '3×/wk',
  '2x_week': '2×/wk',
  '1x_week': '1×/wk',
  biweekly: 'Every 2 wk',
  monthly: 'Monthly',
};

export const FREQ_PER_WEEK: Record<FrequencyKey, number> = {
  daily: 7,
  '5on2off': 5,
  '3x_week': 3,
  '2x_week': 2,
  '1x_week': 1,
  biweekly: 0.5,
  monthly: 0.25,
};

export const TIER_NAMES: Record<TierKey, string> = {
  micro: 'Micro',
  low: 'Low',
  moderate: 'Standard',
  high: 'High',
  veryhigh: 'Very High',
};

export const TIER_ICONS: Record<TierKey, string> = {
  micro: '🟢',
  low: '🟩',
  moderate: '🔵',
  high: '🟠',
  veryhigh: '🔴',
};

export const TIER_COLORS: Record<TierKey, { border: string; text: string; bg: string }> = {
  micro: { border: 'border-emerald-400', text: 'text-emerald-400', bg: 'bg-emerald-400/5' },
  low: { border: 'border-green-400', text: 'text-green-400', bg: 'bg-green-400/5' },
  moderate: { border: 'border-blue-400', text: 'text-blue-400', bg: 'bg-blue-400/5' },
  high: { border: 'border-orange-400', text: 'text-orange-400', bg: 'bg-orange-400/5' },
  veryhigh: { border: 'border-rose-400', text: 'text-rose-400', bg: 'bg-rose-400/5' },
};

export const CATEGORIES: Record<CategoryKey, { label: string }> = {
  all: { label: 'All' },
  weight: { label: 'Weight Mgmt' },
  gh: { label: 'GH Secretagogues' },
  healing: { label: 'Healing' },
  metabolic: { label: 'Metabolic' },
  sexual: { label: 'Sexual Health' },
};

export const COMPOUND_COLORS = [
  '#4f8fff', '#34d399', '#fbbf24', '#a78bfa', '#fb7185', '#22d3ee',
  '#ec4899', '#a3e635', '#fb923c', '#6366f1', '#14b8a6', '#e879f9',
];

export const PRESETS: Record<string, PeptidePreset> = {
  semaglutide: {
    name: 'Semaglutide', category: 'weight', conc: 5, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'fda', regNote: 'FDA-approved (Wegovy/Ozempic). Compounded legally available from 503A/503B.',
    weightBased: false, weightNote: 'FDA fixed-dose titration. Not weight-based. Escalation by tolerability.',
    synergy: [{ key: 'tirzepatide', label: 'Tirzepatide', note: 'Do NOT combine GLP-1 agonists. Choose one.' }],
    recFreq: '1x_week',
    tiers: {
      micro: { dose: 0.05, doseUnit: 'mg', freq: '1x_week', info: '0.05 mg/wk · Microdose protocol · No clinical trial data' },
      low: { dose: 0.25, doseUnit: 'mg', freq: '1x_week', info: '0.25 mg/wk · FDA initiation dose · Wk 1–4 acclimation' },
      moderate: { dose: 1.0, doseUnit: 'mg', freq: '1x_week', info: '1.0 mg/wk · Mid-range (Ozempic) · ~7% weight loss · Titrate over 8+ wk' },
      high: { dose: 2.4, doseUnit: 'mg', freq: '1x_week', info: '2.4 mg/wk · FDA max (Wegovy) · ~15% weight loss (STEP 1) · 20% MACE reduction' },
      veryhigh: { dose: 7.2, doseUnit: 'mg', freq: '1x_week', info: '7.2 mg/wk · STEP UP Phase 3b · 20.7% loss · Dysesthesia 23% · GI 71%' },
    },
    timeline: { micro: '4+ wk → titrate to 0.25', low: 'Wk 1–4: 0.25 → Wk 5–8: 0.5', moderate: '0.25→0.5→1.0 mg over 8 wk', high: '0.25→0.5→1.0→1.7→2.4 · 4 wk/step · 16–20 wk', veryhigh: 'Extended beyond 2.4 mg · Clinical trial only' },
    warnings: { veryhigh: '⚠️ Phase 3b investigational. GI AEs 71%, dysesthesia ~23%. Not FDA-approved at this level.' },
  },
  tirzepatide: {
    name: 'Tirzepatide', category: 'weight', conc: 10, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'fda', regNote: 'FDA-approved (Zepbound/Mounjaro). Compounded tirzepatide illegal in US since March 2025.',
    weightBased: false, weightNote: 'FDA fixed titration. 16% loss at 5 mg, 22.5% at 15 mg.',
    synergy: [], recFreq: '1x_week',
    tiers: {
      micro: { dose: 1.25, doseUnit: 'mg', freq: '1x_week', info: '1.25 mg/wk · Sub-label fractional · No trial data below 2.5' },
      low: { dose: 2.5, doseUnit: 'mg', freq: '1x_week', info: '2.5 mg/wk · FDA initiation · Wk 1–4' },
      moderate: { dose: 5, doseUnit: 'mg', freq: '1x_week', info: '5 mg/wk · Lowest maintenance · 16% loss (SURMOUNT-1)' },
      high: { dose: 10, doseUnit: 'mg', freq: '1x_week', info: '10 mg/wk · ~20% loss · GI discontinuation 5–6%' },
      veryhigh: { dose: 15, doseUnit: 'mg', freq: '1x_week', info: '15 mg/wk · FDA max · 22.5% loss · 57% achieved ≥20%' },
    },
    timeline: { micro: 'Off-label → 2.5 at wk 4', low: 'Wk 1–4: 2.5 → 5 mg', moderate: '2.5→5 over 4 wk', high: '2.5→5→7.5→10 · 4 wk/step · 12–16 wk', veryhigh: '2.5→5→7.5→10→12.5→15 · 4 wk/step · 20–24 wk' },
    warnings: { micro: '⚠️ No trial data below 2.5 mg. Compounded tirzepatide no longer legal in US.', veryhigh: 'No trials above 15 mg.' },
  },
  retatrutide: {
    name: 'Retatrutide', category: 'weight', conc: 5, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'investigational', regNote: 'Investigational — Phase 3 (TRIUMPH). Triple GLP-1/GIP/Glucagon agonist. Expected 2026–2027.',
    weightBased: false, weightNote: 'Phase 2 fixed-dose. 24% loss at 12 mg/48 wk. Triple agonism unique mechanism.',
    synergy: [], recFreq: '1x_week',
    tiers: {
      micro: { dose: 0.5, doseUnit: 'mg', freq: '1x_week', info: '0.5 mg/wk · Phase 1b lowest · Assessment only' },
      low: { dose: 1, doseUnit: 'mg', freq: '1x_week', info: '1 mg/wk · Standard initiation · Wk 1–4' },
      moderate: { dose: 4, doseUnit: 'mg', freq: '1x_week', info: '4 mg/wk · Mid-range · Titrate 1→2→4 over 8 wk' },
      high: { dose: 8, doseUnit: 'mg', freq: '1x_week', info: '8 mg/wk · ~17% loss at 48 wk (Phase 2) · GI AEs significant' },
      veryhigh: { dose: 12, doseUnit: 'mg', freq: '1x_week', info: '12 mg/wk · Max studied · ~24% loss · 83% ≥15% · GI 73–94% · HR increase' },
    },
    timeline: { micro: 'Phase 1b → advance to 1 mg', low: 'Wk 1–4: 1 mg → Wk 5–8: 2 mg', moderate: '1→2→4 over 8 wk (4 wk/step)', high: '1→2→4→8 over 12 wk · 4 wk/step', veryhigh: '1→2→4→8→12 · 4 wk/step · 16–20 wk' },
    warnings: { high: '⚠️ Investigational. GI AEs significant at 8+ mg.', veryhigh: '⚠️ Max Phase 2 dose. GI 73–94%. Dose-dependent HR increase. No long-term safety data.' },
  },
  sermorelin: {
    name: 'Sermorelin', category: 'gh', conc: 7.5, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'research', regNote: 'Previously FDA-approved as Geref (1997, discontinued 2008). GHRH 1-29 analog.',
    weightBased: true, weightNote: 'Historical pediatric: 30 mcg/kg/day. Older adults may need upper range. Self-regulated by somatostatin.',
    synergy: [{ key: 'ipamorelin', label: 'Ipamorelin', note: 'GHRH + GHRP synergistically amplifies GH pulse. Reduce Ipamorelin to 100–150 mcg when stacking.' }],
    recFreq: '5on2off',
    tiers: {
      micro: { dose: 100, doseUnit: 'mcg', freq: '5on2off', info: '100 mcg/night · 5 on/2 off · Minimum effective' },
      low: { dose: 200, doseUnit: 'mcg', freq: '5on2off', info: '200 mcg/night · Geref PI range · General wellness · 3–6 mo' },
      moderate: { dose: 300, doseUnit: 'mcg', freq: '5on2off', info: '300 mcg/night · Most widely prescribed · JCEM: significant GH/IGF-1 increase' },
      high: { dose: 500, doseUnit: 'mcg', freq: 'daily', info: '500 mcg/night daily · Max pituitary stimulation · Water retention possible' },
      veryhigh: { dose: 1000, doseUnit: 'mcg', freq: 'daily', info: '1,000 mcg (1 mg)/night · Defy Medical "max stimulation" · Transition to 2–3×/wk after' },
    },
    timeline: { micro: '3–6 mo → check IGF-1 → increase to 200', low: '3–6 mo · 5 on/2 off prevents desensitization', moderate: '8–12 wk · IGF-1 at wk 4–6 · Cycle off 4 wk', high: '12-wk cycles · Check IGF-1 at 4 & 8 wk', veryhigh: '6–12 mo → taper to 500 mcg or 1 mg 2–3×/wk' },
    warnings: { veryhigh: 'Water retention, joint stiffness. Anti-GRF antibodies possible.' },
  },
  tesamorelin: {
    name: 'Tesamorelin', category: 'gh', conc: 6, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'fda', regNote: 'FDA-approved 2010 (Egrifta) for HIV lipodystrophy.',
    weightBased: false, weightNote: 'FDA fixed. 2 mg = −16% VAT vs −4% for 1 mg. No data above 2 mg/day.',
    synergy: [{ key: 'ipamorelin', label: 'Ipamorelin', note: 'Stack for synergistic GH release. Bedtime together.' }],
    recFreq: '5on2off',
    tiers: {
      micro: { dose: 0.5, doseUnit: 'mg', freq: '5on2off', info: '0.5 mg/night · Off-label cost-driven · Likely sub-therapeutic' },
      low: { dose: 1, doseUnit: 'mg', freq: '5on2off', info: '1 mg/night · Baker 2012 cognition trial · Safe but less effective' },
      moderate: { dose: 2, doseUnit: 'mg', freq: '5on2off', info: '2 mg/night · 5 on/2 off · FDA-equivalent · −15.2% VAT (NEJM 2007)' },
      high: { dose: 2, doseUnit: 'mg', freq: 'daily', info: '2 mg/night daily · Full FDA schedule · Antibodies ~50% at 26 wk' },
      veryhigh: { dose: 2, doseUnit: 'mg', freq: 'daily', info: '2 mg/night daily year-round · Extended beyond trial data · NOT >2 mg' },
    },
    timeline: { micro: '12–26 wk → increase to 1 mg', low: '4 wk at 1 mg → increase to 2 mg', moderate: '12–24 wk · Monitor IGF-1 & glucose q4–8wk', high: 'Continuous · Reassess VAT at 26 wk', veryhigh: 'Continuous · Long-term monitoring · Effect reverses on cessation' },
    warnings: { veryhigh: '⚠️ No data above 2 mg/day. Animal toxicity at 2–4× exposure. 2 mg is effective maximum.' },
  },
  ipamorelin: {
    name: 'Ipamorelin', category: 'gh', conc: 6, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'research', regNote: 'Not FDA-approved. Selective GHRP. No cortisol/prolactin increase.',
    weightBased: false, weightNote: 'Flat dosing 200–300 mcg. GH release plateaus at higher doses.',
    synergy: [
      { key: 'sermorelin', label: 'Sermorelin', note: 'Classic synergistic GH stack. Reduce Ipamorelin to 100–150 mcg when paired.' },
      { key: 'bpc157', label: 'BPC-157', note: 'Healing + GH support. No dose adjustment.' },
    ],
    recFreq: 'daily',
    tiers: {
      micro: { dose: 50, doseUnit: 'mcg', freq: 'daily', info: '50 mcg/day · Sub-therapeutic test · Bedtime fasted' },
      low: { dose: 100, doseUnit: 'mcg', freq: 'daily', info: '100 mcg/day · Conservative · 8–12 wk cycle' },
      moderate: { dose: 200, doseUnit: 'mcg', freq: 'daily', info: '200 mcg/day · Standard protocol · Often with CJC-1295 · 8–12 wk' },
      high: { dose: 300, doseUnit: 'mcg', freq: 'daily', info: '300 mcg/day · Upper standard · Monitor IGF-1 · 8–12 wk with 8–10 wk off' },
      veryhigh: { dose: 300, doseUnit: 'mcg', freq: '3x_week', info: '300 mcg 2–3×/day (up to 900 mcg) · GH plateaus · Monitor IGF-1' },
    },
    timeline: { micro: '2 wk → increase to 100–200 mcg', low: 'Wk 1–2: 100 → 200 if tolerated', moderate: '8–12 wk · IGF-1 at wk 6 · Cycle off 8–10 wk', high: '8–12 wk · Monitor IGF-1 · Cycle off 8–10 wk', veryhigh: '8–12 wk max · Diminishing returns above 300 mcg single dose' },
    warnings: { veryhigh: 'GH release plateaus. Multiple daily injections needed >300 mcg/day.' },
  },
  bpc157: {
    name: 'BPC-157', category: 'healing', conc: 10, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'cat2', regNote: 'FDA Category 2 — prohibited from compounding since late 2023. Half-life <30 min.',
    weightBased: true, weightNote: 'Research: 1–10 mcg/kg/day. HED ~100–150 mcg/day for 80 kg. Bell-shaped dose-response.',
    synergy: [
      { key: 'tb500', label: 'TB-500', note: 'Gold-standard healing stack. BPC-157 (local) + TB-500 (systemic).' },
      { key: 'ghk_cu', label: 'GHK-Cu', note: 'GHK-Cu (collagen) + BPC-157 (tissue repair). No dose adjustment.' },
    ],
    recFreq: 'daily',
    tiers: {
      micro: { dose: 100, doseUnit: 'mcg', freq: 'daily', info: '100–150 mcg/day · HED from rat 10 mcg/kg · Huberman Lab minimum' },
      low: { dose: 250, doseUnit: 'mcg', freq: 'daily', info: '250 mcg/day · Standard starting · 1–2 wk improvements · 4–8 wk cycle' },
      moderate: { dose: 500, doseUnit: 'mcg', freq: 'daily', info: '500 mcg/day (250 BID) · Dominant clinic protocol · Bell-curve: diminishing above this' },
      high: { dose: 750, doseUnit: 'mcg', freq: 'daily', info: '750 mcg/day · Upper clinical · Split AM/PM · 4–6 wk max' },
      veryhigh: { dose: 1000, doseUnit: 'mcg', freq: 'daily', info: '1,000+ mcg/day · Bell-curve may reduce efficacy · Angiogenesis concern' },
    },
    timeline: { micro: '4–8 wk → reassess', low: 'Wk 1–6 at 250 → increase if needed', moderate: 'Wk 1–8 at 500 → escalate 4–6 wk if needed', high: '4–6 wk max → taper to moderate', veryhigh: '2–4 wk max → mandatory taper · 6 wk on/off' },
    warnings: { high: 'Bell-shaped dose-response — higher may reduce efficacy.', veryhigh: '⚠️ Far exceeds standard. Bell-curve response. Angiogenesis/tumor concern. FDA Cat 2 prohibited.' },
  },
  tb500: {
    name: 'TB-500', category: 'healing', conc: 10, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'cat2', regNote: 'FDA Category 2 — prohibited. Thymosin Beta-4 fragment. Phase I: 1,260 mg IV daily × 14 days = safe.',
    weightBased: false, weightNote: 'Protocol-based loading/maintenance. Not weight-adjusted.',
    synergy: [{ key: 'bpc157', label: 'BPC-157', note: 'Gold-standard healing stack. TB-500 systemic; BPC-157 local.' }],
    recFreq: '2x_week',
    tiers: {
      micro: { dose: 750, doseUnit: 'mcg', freq: '2x_week', info: '750 mcg 2×/wk (1.5 mg/wk) · Conservative intro' },
      low: { dose: 2, doseUnit: 'mg', freq: '2x_week', info: '2 mg 2×/wk (4 mg/wk) · Conservative loading · 4–6 wk → 2 mg/wk maint' },
      moderate: { dose: 2.5, doseUnit: 'mg', freq: '2x_week', info: '2.5 mg 2–3×/wk (5–7.5 mg/wk) · Standard loading · 4–6 wk → taper' },
      high: { dose: 5, doseUnit: 'mg', freq: '2x_week', info: '5 mg 2×/wk (10 mg/wk) · Aggressive loading · 2–4 wk then step down' },
      veryhigh: { dose: 5, doseUnit: 'mg', freq: 'daily', info: '5+ mg/day (35+ mg/wk) · Far exceeds protocols · Angiogenesis concern' },
    },
    timeline: { micro: '4–6 wk → increase to low loading', low: 'Loading 4–6 wk → Maint 2 mg 1×/wk → off 2–4 wk', moderate: 'Loading 4 wk → Maint 2–4 mg q1–2wk → off', high: 'Loading 4 wk max → step down → off 4 wk', veryhigh: '2–4 wk max → mandatory taper · 6 wk on/off' },
    warnings: { high: 'Less documented. Taper strongly recommended.', veryhigh: '⚠️ Far exceeds protocols. Angiogenesis promoter. FDA Cat 2 prohibited.' },
  },
  ghk_cu: {
    name: 'GHK-Cu', category: 'healing', conc: 5, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'research', regNote: 'Not FDA-approved for injection. Naturally occurring tripeptide-copper complex.',
    weightBased: false, weightNote: 'Each 2 mg = ~0.03 mg copper (trivial). Dose-response plateaus beyond ~2 mg.',
    synergy: [{ key: 'bpc157', label: 'BPC-157', note: 'GHK-Cu (collagen) + BPC-157 (tissue repair). No dose adjustment.' }],
    recFreq: 'daily',
    tiers: {
      micro: { dose: 0.5, doseUnit: 'mg', freq: '5on2off', info: '0.5 mg 5×/wk · Titration start · Skin hydration by wk 3–4' },
      low: { dose: 1, doseUnit: 'mg', freq: '5on2off', info: '1 mg/day 5×/wk · Most cited starting · 8–12 wk' },
      moderate: { dose: 2, doseUnit: 'mg', freq: '5on2off', info: '2 mg/day 5×/wk · Standard collagen protocol · 8–16 wk' },
      high: { dose: 3, doseUnit: 'mg', freq: '5on2off', info: '3 mg/day · Plateaus beyond ~2 mg · 4–8 wk max' },
      veryhigh: { dose: 5, doseUnit: 'mg', freq: '5on2off', info: '5 mg/day · Minimal benefit over 2–3 mg · Mandatory labs' },
    },
    timeline: { micro: '4–8 wk → increase to 1 mg', low: '8–12 wk (30 on/14 off)', moderate: '8–16 wk with cycling · Monitor copper/ceruloplasmin', high: '4–8 wk max · No concurrent copper supplements', veryhigh: '≤4 wk mandatory · Pre- and mid-cycle labs' },
    warnings: { high: "Plateaus beyond ~2 mg. Wilson's disease is absolute contraindication.", veryhigh: '⚠️ Minimal benefit over 2–3 mg. Cumulative copper concern.' },
  },
  nad: {
    name: 'NAD+', category: 'metabolic', conc: 100, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'research', regNote: 'Not FDA-approved. One published PK study: Grant et al. 2019 (750 mg IV).',
    weightBased: false, weightNote: 'Start low for flush response. Flush = NAD+ → nicotinamide → histamine.',
    synergy: [], recFreq: 'daily',
    tiers: {
      micro: { dose: 10, doseUnit: 'mg', freq: 'daily', info: '10 mg/day SubQ · Minimal flush · Test dose' },
      low: { dose: 25, doseUnit: 'mg', freq: 'daily', info: '25 mg/day SubQ · Low-dose · Slow push · 4–8 wk' },
      moderate: { dose: 50, doseUnit: 'mg', freq: 'daily', info: '50 mg/day SubQ · Standard · Moderate flush · Split AM/PM · 4–8 wk' },
      high: { dose: 100, doseUnit: 'mg', freq: 'daily', info: '100 mg/day SubQ · Significant flush · Clinical supervision · 4–6 wk' },
      veryhigh: { dose: 200, doseUnit: 'mcg', freq: '3x_week', info: '200 mg 3×/wk SubQ or 250–750 mg IV · Clinical setting only' },
    },
    timeline: { micro: 'Wk 1–2 → increase to 25 if tolerable', low: 'Wk 1–2 at 25 → 50 mg', moderate: '4–8 wk · Cycle off 2–4 wk', high: '4–6 wk · Cycle off 4 wk', veryhigh: 'IV: 1–2×/wk clinical · SubQ: 4 wk max' },
    warnings: { high: 'Significant flush. Not recommended without supervision.', veryhigh: '⚠️ Only one published PK study. Limited high-dose SubQ safety data.' },
  },
  motsc: {
    name: 'MOTS-c', category: 'metabolic', conc: 5, concUnit: 'mg/mL', vial: 2, route: 'SubQ',
    regulatory: 'research', regNote: 'Not FDA-approved. Mitochondrial peptide (AMPK activator). FDA flagged for safety risks.',
    weightBased: false, weightNote: 'AMPK activator similar to metformin. Enhances glucose uptake.',
    synergy: [], recFreq: '2x_week',
    tiers: {
      micro: { dose: 1, doseUnit: 'mg', freq: '1x_week', info: '1 mg 1×/wk · Intro · Assess AMPK response · 4 wk' },
      low: { dose: 2.5, doseUnit: 'mg', freq: '1x_week', info: '2.5 mg 1×/wk · Conservative · Exercise mimetic · 4 wk on/2–4 off' },
      moderate: { dose: 2.5, doseUnit: 'mg', freq: '2x_week', info: '2.5 mg 2×/wk (5 mg/wk) · Standard · Mon/Thu · 4 wk on/2–4 off' },
      high: { dose: 5, doseUnit: 'mg', freq: '2x_week', info: '5 mg 2×/wk (10 mg/wk) · Advanced · 3–4 wk cycles' },
      veryhigh: { dose: 10, doseUnit: 'mg', freq: '2x_week', info: '10 mg 2×/wk (20 mg/wk) · Aggressive · 2–3 wk max' },
    },
    timeline: { micro: '4 wk → 2 wk off → increase to 2.5', low: '4 wk → 2–4 wk off → 2×/wk', moderate: '4 wk on → 2–4 wk off → repeat', high: '3–4 wk on → 4 wk off mandatory', veryhigh: '2–3 wk on → 4 wk off' },
    warnings: { high: 'FDA flagged MOTS-c for potential safety risks.', veryhigh: '⚠️ Minimal data. FDA safety concerns. Very short cycles only.' },
  },
  pt141: {
    name: 'PT-141', category: 'sexual', conc: 2, concUnit: 'mg/mL', vial: 5, route: 'SubQ',
    regulatory: 'fda', regNote: 'FDA-approved June 2019 as Vyleesi (1.75 mg SubQ PRN) for premenopausal HSDD.',
    weightBased: false, weightNote: 'PRN dosing. FDA: ≤8 doses/month, ≤1 dose/24 hrs.',
    synergy: [], recFreq: '2x_week',
    tiers: {
      micro: { dose: 0.25, doseUnit: 'mg', freq: '2x_week', info: '0.25 mg PRN · Below Phase 2 · May not be meaningful' },
      low: { dose: 0.75, doseUnit: 'mg', freq: '2x_week', info: '0.75 mg PRN · Phase 2 dose · Mild nausea ~10–15%' },
      moderate: { dose: 1.75, doseUnit: 'mg', freq: '2x_week', info: '1.75 mg PRN · FDA Vyleesi · ≤1/24 hrs · Nausea ~40%' },
      high: { dose: 2, doseUnit: 'mg', freq: '2x_week', info: '2 mg PRN · Off-label male ED · Nausea increases · BP +3–6 mmHg' },
      veryhigh: { dose: 3, doseUnit: 'mg', freq: '2x_week', info: '3–4 mg PRN · No formal safety data >1.75 · Priapism risk · Hyperpigmentation' },
    },
    timeline: { micro: 'Start 0.25 → increase to 0.75', low: 'PRN · If insufficient, increase to 1.75', moderate: 'PRN · Max 8 doses/month', high: 'PRN only · No more than 1/24 hrs', veryhigh: 'PRN only · ≤2×/wk' },
    warnings: { high: 'Nausea severity increases. Spontaneous erections up to 24 hrs. Hyperpigmentation.', veryhigh: '⚠️ No formal SubQ data >1.75 mg. Permanent hyperpigmentation. Avoid in CV disease.' },
  },
};
